import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { MainProductEntry } from '@telialt/common-order-cart/src/entries/MainProductEntry';
import { ContractType, EntryWithDevice } from '@telialt/models/src/EshopModel';
import {
    selectInitialPayment,
    selectLeasingPlan,
    selectLeasingPlanDuration,
    selectRentPlan,
} from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';

import { formatVariantOptionValue } from '../../helpers/formatVariantOptionValue';
import { useCartQuery } from '../../hooks/queries/useCartQuery';
import { useIsLocalEnvironment } from '../../hooks/useIsLocalEnvironment';
import { toSmallImage } from '../utils';

export const MainProductEntryConnected: FC<{ entry: EntryWithDevice }> = ({ entry }) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const isLocal = useIsLocalEnvironment();
    const localCode = isLocal && cart?.offerCode;

    const productVariantOptions = entry.product.baseOptions?.[0]?.selected?.variantOptionQualifiers
        ?.map(formatVariantOptionValue)
        .filter(Boolean)
        .join(', ');

    const isRenting = cart?.contractType === ContractType.Renting;
    const isMonthly = Boolean(selectLeasingPlan(cart)) || isRenting;

    const rentPlan = selectRentPlan(cart);

    const initialPayment = selectInitialPayment(cart) ? entry.totalPrice : undefined;
    const discount = entry.serviceBundleDiscount && {
        value: -entry.serviceBundleDiscount.value,
        formattedValue: `-${entry.serviceBundleDiscount.formattedValue}`,
    };
    const leasingPlanDurationInMonths = selectLeasingPlanDuration(cart);
    const rentCommitmentInMonths = rentPlan?.monthValue;

    const monthlyPrice = isRenting ? rentPlan?.monthlyPrice : entry.monthlyPrice;
    const residualPrice = isRenting ? entry.rentingPlanEntry?.residualPrice : undefined;

    const period = isRenting ? rentCommitmentInMonths : leasingPlanDurationInMonths;
    const commitmentText = period
        ? withParams(t['eshop.order_cart.entry_main_product.commitment_note'], { period })
        : undefined;

    return (
        <MainProductEntry
            name={entry.product.name}
            image={toSmallImage(entry.product.images)}
            variantOptions={productVariantOptions}
            monthlyPrice={isMonthly ? monthlyPrice : undefined}
            fullPrice={isMonthly ? undefined : entry.totalPrice}
            localCode={localCode}
            initialPayment={initialPayment}
            copyrightFee={entry.copyrightFee}
            residualPrice={residualPrice}
            discount={discount}
            footNote={commitmentText}
        />
    );
};
